
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { useForm } from "react-hook-form";
import { useToast } from "@/hooks/use-toast";

interface School {
  id: string;
  subscription_id: string;
  school_name: string;
  email: string;
  phone?: string;
  status: 'active' | 'inactive' | 'expired';
  subscription_end?: string;
  students_count: number;
  created_at: string;
  updated_at: string;
}

interface SubscriptionForm {
  months: number;
}

interface ExtendSubscriptionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  school: School | null;
  onConfirm: (months: number) => void;
}

const ExtendSubscriptionDialog = ({ 
  open, 
  onOpenChange, 
  school, 
  onConfirm 
}: ExtendSubscriptionDialogProps) => {
  const { toast } = useToast();
  const subscriptionForm = useForm<SubscriptionForm>();

  const onSubmit = (data: SubscriptionForm) => {
    if (!school) return;
    
    onConfirm(data.months);
    subscriptionForm.reset();
    onOpenChange(false);
    
    toast({
      title: "تم تمديد الاشتراك",
      description: `تم تمديد اشتراك ${school.school_name} لمدة ${data.months} شهر`,
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md [&>button]:hidden" dir="rtl">
        <DialogHeader className="bg-green-50 p-4 rounded-t-lg -mx-6 -mt-6 mb-4">
          <DialogTitle className="text-green-600 text-right">
            تمديد الاشتراك
          </DialogTitle>
        </DialogHeader>
        <DialogDescription className="text-gray-700 text-right mb-4">
          تمديد اشتراك {school?.school_name}
        </DialogDescription>
        <Form {...subscriptionForm}>
          <form onSubmit={subscriptionForm.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={subscriptionForm.control}
              name="months"
              rules={{ 
                required: "عدد الأشهر مطلوب",
                min: { value: 1, message: "أقل عدد شهر واحد" },
                max: { value: 24, message: "أقصى عدد 24 شهر" }
              }}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>عدد الأشهر</FormLabel>
                  <FormControl>
                    <Input
                      type="number"
                      min="1"
                      max="24"
                      placeholder="6"
                      {...field}
                      onChange={(e) => field.onChange(parseInt(e.target.value))}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <div className="flex justify-end gap-2 pt-4">
              <Button type="submit" className="bg-green-600 hover:bg-green-700 text-white min-w-[120px]">
                تمديد الاشتراك
              </Button>
              <Button 
                type="button"
                variant="outline" 
                onClick={() => {
                  onOpenChange(false);
                  subscriptionForm.reset();
                }}
                className="bg-gray-100 hover:bg-gray-200 text-gray-800 min-w-[120px]"
              >
                إغلاق
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};

export default ExtendSubscriptionDialog;
